'use client';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faPlus, faSliders, faTrash, faXmark } from '@fortawesome/pro-light-svg-icons';
import { cn } from '@shared/utils/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button';
import { Combobox, SmartCombobox } from './components';
import { useFilterConditions } from './hooks';
import type { AdvancedFilterProps, AdvancedFilterTranslations } from './types';

/**
 * Componente AdvancedFilterMobile - Versión mobile del sistema de filtros avanzados
 *
 * Versión optimizada para dispositivos móviles con lógica de colapso/expansión.
 * Los filtros activos se muestran como chips colapsados en la parte superior.
 *
 * Características:
 * - Estado colapsado: Muestra filtros activos como chips
 * - Estado expandido: Formulario completo para editar/agregar
 * - Transición suave entre estados
 * - Layout optimizado para pantallas pequeñas
 * - Reutiliza la misma lógica de useFilterConditions
 *
 * @param filterOptions - Configuración de columnas, acciones y valores disponibles
 * @param onChange - Callback ejecutado cuando cambian las condiciones válidas
 * @param initialConditions - Condiciones iniciales para hidratar el estado
 * @param translations - Textos traducidos opcionales para override específico
 */
const AdvancedFilterMobile = ({ filterOptions, onChange, initialConditions, translations }: AdvancedFilterProps) => {
  const modalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado para el filtro en construcción (temporal)
  const [tempFilter, setTempFilter] = useState({
    column: '',
    action: '',
    value: '',
  });

  // 🎯 Estado para cachear opciones dinámicas
  const [dynamicOptionsCache, setDynamicOptionsCache] = useState<Record<string, { label: string; value: string }[]>>(
    {},
  );

  // Estado para controlar la visibilidad del constructor de filtros
  const [isFilterBuilderVisible, setIsFilterBuilderVisible] = useState(true);

  // 🌍 Hook de traducción para namespace UI compartido
  const { t: tUI } = useTranslation('ui');

  // 🎯 Traducciones híbridas: por defecto desde 'ui' namespace + override opcional
  const t: AdvancedFilterTranslations = {
    filters: translations?.filters || tUI('advancedFilter.filters'),
    fulfillsWith: translations?.fulfillsWith || tUI('advancedFilter.fulfillsWith'),
    selectColumn: translations?.selectColumn || tUI('advancedFilter.selectColumn'),
    selectAction: translations?.selectAction || tUI('advancedFilter.selectAction'),
    selectValue: translations?.selectValue || tUI('advancedFilter.selectValue'),
    addCondition: translations?.addCondition || tUI('advancedFilter.addCondition'),
    notFound: translations?.notFound || tUI('advancedFilter.notFound'),
    and: translations?.and || tUI('advancedFilter.and'),
    or: translations?.or || tUI('advancedFilter.or'),
    enterText: translations?.enterText || tUI('advancedFilter.enterText', 'Escribir texto'),
    selectDate: translations?.selectDate || tUI('advancedFilter.selectDate', 'Seleccionar fecha'),
    selectDateRange: translations?.selectDateRange || tUI('advancedFilter.selectDateRange', 'Seleccionar rango'),
    from: translations?.from || tUI('advancedFilter.from', 'Desde'),
    to: translations?.to || tUI('advancedFilter.to', 'Hasta'),
  };

  const { conditions, activeFiltersCount, addCondition, removeCondition } = useFilterConditions(
    filterOptions,
    onChange,
    initialConditions,
  );

  // Utilidad: dado un listado de opciones heterogéneas, encontrar el label por el id/value
  const resolveLabelFromOptions = (options: Array<any> | undefined, targetValue: string): string | null => {
    if (!options || !Array.isArray(options)) return null;
    const found = options.find((opt: any) => {
      const optValue = opt?.value ?? opt?.id;
      return String(optValue) === String(targetValue);
    });
    if (!found) return null;
    return found?.label ?? found?.value ?? found?.name ?? null;
  };

  // 🚀 Precarga de opciones dinámicas para condiciones existentes sin valueLabel
  useEffect(() => {
    const loadOptionsForMissingLabels = async () => {
      const columnsNeedingOptions = Array.from(
        new Set(
          conditions.filter((c) => !!c.value && !c.valueLabel && !dynamicOptionsCache[c.column]).map((c) => c.column),
        ),
      );

      if (columnsNeedingOptions.length === 0) return;

      await Promise.all(
        columnsNeedingOptions.map(async (columnKey) => {
          const filter = filterOptions.find((f) => f.column.value === columnKey);
          if (!filter?.loadValues) return;
          try {
            const options = await filter.loadValues(columnKey);
            setDynamicOptionsCache((prev) => ({
              ...prev,
              [columnKey]: options,
            }));
          } catch (err) {
            console.error('Error precargando opciones dinámicas:', err);
          }
        }),
      );
    };

    loadOptionsForMissingLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditions, filterOptions]);

  // 🎯 Obtener filtros activos para mostrar en estado colapsado
  const activeFilters = conditions.filter((c) => {
    if (!c.column || !c.action) return false;
    if (c.action === 'is_null' || c.action === 'is_not_null') return true;
    return Boolean(c.value);
  });

  // 🎯 Función para obtener el label de un filtro activo
  const getFilterLabel = (condition: (typeof conditions)[0]) => {
    const selectedFilter = filterOptions.find((f) => f.column.value === condition.column);
    const selectedAction = selectedFilter?.actions.find((a) => a.value === condition.action);

    const columnLabel = selectedFilter?.column.label || condition.column;
    const actionLabel = selectedAction?.label || condition.action;

    // 🎯 Usar valueLabel solo si es diferente del ID; de lo contrario, intentar resolver
    let valueLabel =
      condition.valueLabel && condition.valueLabel !== condition.value ? condition.valueLabel : condition.value;

    // Si no hay valueLabel, buscar en opciones estáticas (aceptando {label,value} o {id,value})
    if (!condition.valueLabel && selectedFilter?.values) {
      const resolved = resolveLabelFromOptions(selectedFilter.values as any[], condition.value);
      if (resolved) valueLabel = resolved;
      // resolved static
    }

    // Si no hay valueLabel, buscar en cache de opciones dinámicas
    if (!condition.valueLabel && !selectedFilter?.values && dynamicOptionsCache[condition.column]) {
      const resolved = resolveLabelFromOptions(dynamicOptionsCache[condition.column] as any[], condition.value);
      if (resolved) valueLabel = resolved;
      // resolved via dynamic cache
    }

    if (condition.action === 'is_null' || condition.action === 'is_not_null') {
      return (
        <div className="flex items-center w-full text-text">
          <div className="w-32 text-left overflow-hidden text-ellipsis whitespace-nowrap">{columnLabel}</div>
          <div className="w-20 text-center overflow-hidden text-ellipsis whitespace-nowrap">{actionLabel}</div>
        </div>
      );
    }

    return (
      <div className="flex items-center w-full text-text">
        <div className="w-full text-left overflow-hidden text-ellipsis whitespace-nowrap">{columnLabel}</div>
        <div className="w-full text-center overflow-hidden text-ellipsis whitespace-nowrap">{actionLabel}</div>
        <div className="w-full text-right overflow-hidden text-ellipsis whitespace-nowrap">{valueLabel}</div>
      </div>
    );
  };

  // 🎯 Funciones para manejar el modal
  const handleOpenModal = () => {
    modalRef.current?.openModal();
  };

  // 🎯 Función para actualizar el filtro temporal
  const updateTempFilter = (field: 'column' | 'action' | 'value', value: string) => {
    setTempFilter((prev) => ({
      ...prev,
      [field]: value,
      // Reset dependent fields when column changes
      ...(field === 'column' && { action: '', value: '' }),
      ...(field === 'action' && { value: '' }),
    }));
  };

  // 🎯 Función para agregar el filtro temporal a las condiciones
  const handleAddCondition = async () => {
    // Si el constructor no está visible, solo lo mostramos
    if (!isFilterBuilderVisible) {
      setIsFilterBuilderVisible(true);
      return;
    }

    // Si está visible, procedemos con la lógica normal
    if (
      tempFilter.column &&
      tempFilter.action &&
      (tempFilter.value || tempFilter.action === 'is_null' || tempFilter.action === 'is_not_null')
    ) {
      // 🎯 Buscar el filtro seleccionado para obtener loadValues
      const selectedFilter = filterOptions.find((f) => f.column.value === tempFilter.column);

      let valueLabel: string | undefined = undefined;

      // 🎯 Si hay valores estáticos, resolver directamente
      if (Array.isArray(selectedFilter?.values) && tempFilter.value) {
        const resolved = resolveLabelFromOptions(selectedFilter!.values as any[], tempFilter.value);
        if (resolved) valueLabel = resolved;
      } else if (selectedFilter?.loadValues && tempFilter.value) {
        // 🎯 Si tiene loadValues, cargar las opciones para obtener el label
        try {
          // Verificar si ya tenemos las opciones en cache
          if (!dynamicOptionsCache[tempFilter.column]) {
            const options = await selectedFilter.loadValues(tempFilter.column);
            setDynamicOptionsCache((prev) => ({
              ...prev,
              [tempFilter.column]: options,
            }));

            // Buscar el label en las opciones cargadas
            const resolved = resolveLabelFromOptions(options as any[], tempFilter.value);
            if (resolved) valueLabel = resolved;
            // resolved after loading options
          } else {
            // Usar cache existente
            const resolved = resolveLabelFromOptions(dynamicOptionsCache[tempFilter.column] as any[], tempFilter.value);
            if (resolved) valueLabel = resolved;
            // resolved from existing cache
          }
        } catch (error) {
          console.error('Error loading dynamic options:', error);
          // Si falla, usar el valor original
        }
      }

      // 🎯 Agregar nueva condición directamente con todos los datos
      const newCondition = {
        column: tempFilter.column,
        action: tempFilter.action,
        value: tempFilter.value,
        valueLabel: valueLabel, // 🎯 Incluir el label del valor si fue resuelto
        logic: 'y' as const,
      };

      addCondition(newCondition);

      // Resetear filtro temporal
      setTempFilter({
        column: '',
        action: '',
        value: '',
      });
    }
  };

  return (
    <>
      {/* 🎯 Botón trigger para abrir el modal */}
      <div className="relative">
        <Button
          variant="outline"
          type="button"
          size="sm"
          className={cn(
            'shadow-none transition-colors border border-input bg-background text-foreground/70 hover:bg-accent hover:text-accent-foreground',
          )}
          icon={faSliders}
          iconPosition="start"
          onClick={handleOpenModal}
          data-testid="filter-open-button-mobile"
        ></Button>
        {/* 🎯 Badge de contador de filtros activos */}
        {activeFiltersCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm">
            {activeFiltersCount}
          </span>
        )}
      </div>

      {/* 🎯 Modal de pantalla completa para filtros móviles */}
      <ModalCenter ref={modalRef} title={t.filters} type="default" width="xl" className="w-full h-full max-w-none">
        <div className="space-y-4">
          {/* Active Filters Display - Mostrar filtros activos */}
          {activeFilters.length > 0 && (
            <div className="space-y-2 border-t border-lines pt-4">
              {activeFilters.map((filter) => (
                <div key={filter.id} className="flex items-center gap-4 min-h-[2.5rem]">
                  {/* Filter Chip - Chip del filtro */}
                  <div className="bg-card-bg border border-card-border rounded-lg px-3 py-2 flex-1 max-w-[calc(100%-2rem)] flex items-center min-h-[2.5rem]">
                    {getFilterLabel(filter)}
                  </div>
                  {/* Remove Button - Botón eliminar fuera del chip */}
                  <Button
                    type="button"
                    size="sm"
                    variant="icon"
                    icon={faXmark}
                    className="bg-transparent hover:bg-accent hover:text-accent-foreground transition-colors shadow-none w-6 h-6 flex-shrink-0"
                    onClick={() => removeCondition(filter.id)}
                    data-testid={`filter-remove-mobile-${filter.id}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Filter Builder - Constructor de filtros */}
          <div className="space-y-3 border-t border-lines pt-3">
            {/* Sección "Cumple con" y botón trash solo visible cuando el constructor está visible */}
            {isFilterBuilderVisible && (
              <div className="font-medium pb-3 pt-2 justify-between flex items-center">
                {t.fulfillsWith}
                {/* Botón trash solo visible cuando hay filtros aplicados */}
                {activeFilters.length > 0 && (
                  <Button
                    variant="outline"
                    size="default"
                    icon={faTrash}
                    onClick={() => setIsFilterBuilderVisible(!isFilterBuilderVisible)}
                    data-testid="filter-toggle-builder-mobile"
                  />
                )}
              </div>
            )}

            {/* Filter Fields - Campos del filtro */}
            {isFilterBuilderVisible && (
              <div className="space-y-3">
                {/* Column Selection - Selección de columna */}
                <div>
                  <Combobox
                    options={filterOptions.map((f) => f.column)}
                    placeholder={t.selectColumn}
                    value={tempFilter.column}
                    setValue={(val) => updateTempFilter('column', val)}
                    dataTestId="temp-filter-column-mobile"
                  />
                </div>

                {/* Action Selection - Selección de acción */}
                <div>
                  <Combobox
                    options={filterOptions.find((f) => f.column.value === tempFilter.column)?.actions || []}
                    placeholder={t.selectAction}
                    value={tempFilter.action}
                    setValue={(val) => updateTempFilter('action', val)}
                    disabled={!tempFilter.column}
                    dataTestId="temp-filter-action-mobile"
                  />
                </div>

                {/* Value Selection - Selección de valor */}
                <div>
                  {filterOptions.find((f) => f.column.value === tempFilter.column) ? (
                    <SmartCombobox
                      filterOption={filterOptions.find((f) => f.column.value === tempFilter.column)!}
                      value={tempFilter.value}
                      setValue={(val) => updateTempFilter('value', val)}
                      placeholder={t.selectValue}
                      disabled={
                        !tempFilter.column || tempFilter.action === 'is_null' || tempFilter.action === 'is_not_null'
                      }
                      dataTestId="temp-filter-value-mobile"
                      translations={{
                        notFound: t.notFound,
                        enterText: t.enterText,
                        selectDate: t.selectDate,
                        selectDateRange: t.selectDateRange,
                        from: t.from,
                        to: t.to,
                      }}
                    />
                  ) : (
                    <div className="bg-background border border-border rounded px-3 py-2 text-sm text-muted-foreground opacity-70">
                      {t.selectValue}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Botón Añadir Condición - Siempre visible */}
            <div className="pt-2">
              <Button
                type="button"
                size="sm"
                variant="primary"
                bgcolor="transparent"
                className="w-auto ml-auto hover:bg-accent hover:text-accent-foreground text-foreground transition-colors shadow-none"
                icon={faPlus}
                iconPosition="start"
                onClick={handleAddCondition}
                disabled={
                  isFilterBuilderVisible &&
                  (!tempFilter.column ||
                    !tempFilter.action ||
                    (!tempFilter.value && tempFilter.action !== 'is_null' && tempFilter.action !== 'is_not_null'))
                }
                data-testid="filter-add-condition-mobile"
              >
                {t.addCondition}
              </Button>
            </div>
          </div>
        </div>
      </ModalCenter>
    </>
  );
};

export default AdvancedFilterMobile;
